import React, { useState } from 'react';
import { Flag } from 'lucide-react';
import ModerationService from '../services/ModerationService';
import Modal from './ui/Modal.jsx';
import Button from './ui/Button.jsx';

const REASONS = [
  { key: 'BELEIDIGUNG', label: 'Beleidigung' },
  { key: 'MOBBING', label: 'Mobbing' },
  { key: 'SPAM', label: 'Spam / Quatsch' },
  { key: 'UNANGEMESSEN', label: 'Unangemessener Inhalt' },
  { key: 'SONSTIGES', label: 'Sonstiges' },
];

/**
 * ReportModal — gemeinsames Melde-Formular für Sessions und Profile.
 *
 * Props:
 * - targetType: 'SESSION' | 'PROFILE'
 * - targetId: Session-ID bzw. Profil-ID
 * - reportedUserId: User-ID des Gemeldeten (Host bzw. Profil-Inhaber)
 * - targetLabel: Anzeigename fürs Modal (z.B. Session-Titel oder Nutzername)
 * - onClose: () => void
 */
const ReportModal = ({ targetType, targetId, reportedUserId, targetLabel, onClose }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!reason) {
      setError('Bitte wähle einen Grund aus.');
      return;
    }
    try {
      setSubmitting(true);
      setError(null);
      await ModerationService.reportContent({
        targetType,
        targetId,
        reportedUserId,
        reason,
        details: details.slice(0, 500),
      });
      setDone(true);
    } catch (err) {
      setError(err?.message || 'Meldung konnte nicht gesendet werden.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      fullscreenOnMobile
      open
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <Flag className="h-5 w-5 text-danger" />
          {targetType === 'SESSION' ? 'Session melden' : 'Profil melden'}
        </span>
      }
    >
      {done ? (
        <div className="space-y-4">
          <p className="text-foreground">
            Danke für deine Meldung. Eine Lehrkraft schaut sich das an.
          </p>
          <Button variant="success" className="w-full" onClick={onClose}>
            Schließen
          </Button>
        </div>
      ) : (
        <>
          {targetLabel && (
            <p className="mb-3 truncate text-sm text-muted-foreground">
              Betrifft: <span className="font-medium">{targetLabel}</span>
            </p>
          )}

          <div className="mb-4 space-y-2">
            {REASONS.map((r) => (
              <label
                key={r.key}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-colors ${
                  reason === r.key
                    ? 'border-danger bg-danger-soft'
                    : 'border-border hover:bg-foreground/5'
                }`}
              >
                <input
                  type="radio"
                  name="report-reason"
                  value={r.key}
                  checked={reason === r.key}
                  onChange={() => setReason(r.key)}
                  className="accent-danger"
                />
                <span className="text-foreground">{r.label}</span>
              </label>
            ))}
          </div>

          <textarea
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="Was ist passiert? (optional, max. 500 Zeichen)"
            className="mb-3 w-full rounded-xl border border-border bg-surface-raised p-3 text-base text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />

          {error && (
            <p role="alert" className="mb-3 text-sm text-danger">
              {error}
            </p>
          )}

          <div className="flex gap-2">
            <Button variant="secondary" className="flex-1" onClick={onClose} disabled={submitting}>
              Abbrechen
            </Button>
            <Button
              variant="danger"
              className="flex-1"
              onClick={handleSubmit}
              disabled={submitting || !reason}
            >
              {submitting ? 'Wird gesendet…' : 'Melden'}
            </Button>
          </div>
        </>
      )}
    </Modal>
  );
};

export default ReportModal;
